import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  ArrowRight,
  CheckCircle,
  ClipboardCopy,
  Loader,
  AlertTriangle,
  Info,
  ArrowLeft,
  Car,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import API_CONFIG from "../lib/ApiConfig";

/**
 * DimoCallbackScreen - Component to handle DIMO redirect callbacks
 *
 * This component:
 * 1. Parses URL parameters from the redirect
 * 2. Automatically processes the wallet ID
 * 3. Creates/updates DIMO integration using the API
 * 4. Shows success or error state
 */
const DimoCallbackScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getIdToken, user, loading: authLoading } = useAuth();
  const [params, setParams] = useState({});
  const [copied, setCopied] = useState(false);
  const [processingState, setProcessingState] = useState({
    isProcessing: true,
    success: false,
    error: null,
    integration: null,
    vehicles: [],
  });

  // Parse URL parameters on component mount
  useEffect(() => {
    const parseParams = () => {
      try {
        // Parse URL parameters
        const searchParams = new URLSearchParams(location.search);
        const paramObj = {};

        // Convert searchParams to a regular object
        for (const [key, value] of searchParams.entries()) {
          paramObj[key] = value;
        }

        setParams(paramObj);
        console.log("DIMO Callback Parameters:", paramObj);

        // Check for wallet ID (the most critical parameter)
        const walletId = paramObj.walletAddress;

        if (!walletId) {
          setProcessingState({
            isProcessing: false,
            success: false,
            error: "No wallet address was provided by DIMO",
            integration: null,
            vehicles: [],
          });
        }
      } catch (error) {
        console.error("Error parsing parameters:", error);
        setProcessingState({
          isProcessing: false,
          success: false,
          error: error.message || "Unknown error occurred",
          integration: null,
          vehicles: [],
        });
      }
    };

    parseParams();
  }, [location]);

  // Handle authentication and API call separately
  useEffect(() => {
    // Skip if no walletAddress in params or already processed
    if (!params.walletAddress || !processingState.isProcessing) {
      return;
    }
    if (authLoading || !user) {
      return;
    }
    const walletId = params.walletAddress;

    // Function to check auth and make API call
    const checkAuthAndProcess = async () => {
      try {
        // Attempt to get the token
        const token = await getIdToken();

        // If no token yet, retry after a delay
        if (!token) {
          console.log("Authentication not ready yet, waiting...");
          setTimeout(checkAuthAndProcess, 1000); // Retry after 1 second
          return;
        }

        console.log(
          "Authentication token received, proceeding with integration"
        );

        // Create/update DIMO integration
        const url = API_CONFIG.getUrl("api/users/dimo/integration");
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
          body: JSON.stringify({
            userId: "unknown", // This matches the original implementation
            walletId: walletId,
          }),
        });

        if (!response.ok) {
          let errorMessage = `Failed to create DIMO integration: ${response.status}`;

          // Try to parse the error response
          try {
            const errorData = await response.json();
            if (response.status === 409) {
              errorMessage =
                errorData.message ||
                "This DIMO wallet is already connected to a different account";
            } else {
              errorMessage = errorData.message || errorMessage;
            }
          } catch (e) {
            // If parsing fails, use the default message
            if (response.status === 409) {
              errorMessage =
                "This DIMO wallet is already connected to a different account";
            }
          }

          throw new Error(errorMessage);
        }

        // Process successful response
        const integrationData = await response.json();
        console.log("DIMO integration created:", integrationData);

        // Update state with success
        setProcessingState({
          isProcessing: false,
          success: true,
          error: null,
          integration: integrationData,
          vehicles: integrationData.vehicles || [],
        });

        // Store DIMO data in session storage for use in the main flow
        const dimoData = {
          integration: integrationData,
          vehicles: integrationData.vehicles || [],
          connectedAt: new Date().toISOString(),
          skipped: false,
        };
        sessionStorage.setItem("dimoData", JSON.stringify(dimoData));
      } catch (error) {
        console.error("Error processing DIMO integration:", error);
        setProcessingState({
          isProcessing: false,
          success: false,
          error: error.message || "Unknown error occurred",
          integration: null,
          vehicles: [],
        });
      }
    };

    // Start the process
    checkAuthAndProcess();
  }, [params, processingState.isProcessing, getIdToken]);

  // Navigate back to the onboarding flow
  const handleContinue = () => {
    navigate(`/onboarding/dimo`, {
      replace: true,
    });    
  };

  // Copy all parameters to clipboard as JSON
  const copyToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(params, null, 2));
    setCopied(true);

    // Reset "copied" status after 2 seconds
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  // Render each parameter in a readable format (advanced mode)
  const renderParams = () => {
    if (Object.keys(params).length === 0) {
      return <p className="text-gray-500 italic">No parameters found in URL</p>;
    }

    return (
      <div className="space-y-2">
        <div className="flex justify-end mb-2">
          <button
            onClick={copyToClipboard}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            {copied ? (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <ClipboardCopy className="w-4 h-4" />
                <span>Copy all</span>
              </>
            )}
          </button>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-80">
          {Object.entries(params).map(([key, value]) => (
            <div key={key} className="mb-2 last:mb-0">
              <span className="font-medium text-gray-700">{key}: </span>
              <span className="text-gray-900 break-all">{value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render vehicles list if available
  const renderVehicles = () => {
    const { vehicles } = processingState;

    if (!vehicles || vehicles.length === 0) {
      return (
        <div className="text-center p-6 bg-gray-50 rounded-lg">
          <Car className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600">
            No vehicles found in your DIMO account
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {vehicles.map((vehicle, index) => (
          <div
            key={vehicle.id || index}
            className="p-4 border rounded-lg flex items-center gap-3"
          >
            <Car className="w-6 h-6 text-blue-500" />
            <div className="flex-1">
              <p className="font-medium">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render loading state
  if (processingState.isProcessing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
        <div className="w-full max-w-lg">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">
                Processing DIMO Authentication
              </CardTitle>
            </CardHeader>
            <CardContent className="py-10 flex flex-col items-center justify-center">
              <Loader className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <p className="text-gray-600 text-center">
                Please wait while we process your DIMO connection...
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Render error state
  if (!processingState.success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
        <div className="w-full max-w-lg">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">
                DIMO Connection Error
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
              </div>

              <div className="text-center mb-2">
                <p className="text-gray-700 font-medium">
                  We encountered an error while connecting to DIMO:
                </p>
                <p className="text-red-600 mt-2">{processingState.error}</p>
              </div>

              {/* <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-2">
                <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  You can try again by returning to the previous screen and using the popup login method.
                </p>
              </div> */}

              {/* <div className="pt-2">
                <p className="text-sm text-gray-500 mb-4">Advanced debug information:</p>
                {renderParams()}
              </div> */}

              <button
                onClick={handleContinue}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to DIMO Connect
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Render success state
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
      <div className="w-full max-w-lg">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              DIMO Connection Successful
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="text-center mb-2">
              <p className="text-gray-700">
                Your DIMO account has been successfully connected!
              </p>
            </div>

            <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
              <CheckCircle className="w-5 h-5" />
              <span>
                Connected with wallet{" "}
                {processingState.integration?.walletId.substring(0, 6)}...
                {processingState.integration?.walletId.slice(-4)}
              </span>
            </div>

            <div className="pt-2">
              <h3 className="font-medium mb-3">Your Vehicles</h3>
              {renderVehicles()}
            </div>

            <button
              onClick={handleContinue}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue to Onboarding
              <ArrowRight className="w-4 h-4" />
            </button>

            {/* <div className="text-center">
              <button
                onClick={() => {
                  const debugSection = document.getElementById('debug-section');
                  if (debugSection) {
                    debugSection.classList.toggle('hidden');
                  }
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Show Debug Information
              </button>
              
              <div id="debug-section" className="hidden mt-4">
                <p className="text-sm text-gray-500 mb-2">Raw callback parameters:</p>
                {renderParams()}
              </div>
            </div> */}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DimoCallbackScreen;
